import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useConversation, ConversationProvider } from '@elevenlabs/react';
import './VoiceBooking.css';
import { FaFacebook, FaMicrophone, FaStop, FaPhone, FaGlobe, FaClock } from 'react-icons/fa';
import { RiInstagramFill } from 'react-icons/ri';
import { TbWorldWww } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/logo.png';
import { Spin } from 'antd';

const VoiceBookingContent = () => {
    const { businessId, sessionId } = useParams();
    const [businessData, setBusinessData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isStarting, setIsStarting] = useState(false);

    // Language states
    const [language, setLanguage] = useState('English');

    // Initialize ElevenLabs conversation hook
    const conversation = useConversation({
        onConnect: () => {
            console.log('✅ Connected to ElevenLabs agent');
        },
        onDisconnect: () => {
            console.log('🔌 Disconnected from ElevenLabs agent');
        },
        onMessage: (message) => {
            console.log('📨 Message from agent:', message);
        },
        onError: (error) => {
            console.error('❌ Conversation error:', error);
            setError(error.message || 'An error occurred during the conversation');
        },
    });

    // Fetch business details
    useEffect(() => {
        const fetchBusinessData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`https://next-salon-183456c6b9a7.herokuapp.com/business/${businessId}`);
                if (!response.ok) throw new Error('Business not found');
                const data = await response.json();
                setBusinessData(data.data[0]);
                setError(null);
            } catch (err) {
                console.error('Error fetching business:', err);
                setError('Unable to load business information. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };
        if (businessId) fetchBusinessData();
    }, [businessId]);

    // Start conversation with ElevenLabs
    const startConversation = useCallback(async () => {
        try {
            setIsStarting(true);
            setError(null);

            console.log('🔗 Fetching signed URL from backend with businessId:', businessId);

            // Get signed URL from backend with businessId as dynamic variable
            const response = await fetch(
                `https://next-salon-183456c6b9a7.herokuapp.com/elevenlabs/signed-url?businessId=${businessId}&secret=${sessionId}`
            );

            if (!response.ok) {
                throw new Error(`Server returned ${response.status}`);
            }

            const data = await response.json();
            console.log('📦 Backend response:', data);

            if (!data.success || !data.signedUrl) {
                throw new Error('Failed to get signed URL from server');
            }

            console.log('✅ Signed URL received with businessId:', businessId);
            console.log('🎙️ Starting conversation with dynamic businessId...');

            // Start conversation with agentId and pass businessId as dynamic variable
            await conversation.startSession({
                signedUrl: data.signedUrl,
                dynamicVariables: {
                    business_id: businessId,
                    business_name: businessData.name,
                    language: language,
                    timezone: businessData?.timezone || 'UTC',
                },
            });

            console.log('✅ Conversation started successfully');
            console.log('📍 Business ID:', businessId);
            console.log('🌐 Language:', language);
            setIsStarting(false);
        } catch (err) {
            console.error('❌ Error starting conversation:', err);
            setError(err.message || 'Failed to start conversation');
            setIsStarting(false);
        }
    }, [businessId, sessionId, conversation, businessData, language]);

    // End conversation
    const endConversation = useCallback(async () => {
        try {
            console.log('⏹️ Ending conversation...');
            await conversation.endSession();
            console.log('✅ Conversation ended');
        } catch (err) {
            console.error('❌ Error ending conversation:', err);
        }
    }, [conversation]);

    // Get connection status
    const isConnected = conversation.status === 'connected';
    const isConnecting = conversation.status === 'connecting' || isStarting;

    return (
        <div className="voice-booking-container">
            <div className="header">
                <Link className="py-0 py-md-4 me-2 me-md-5" to="/home">
                    <img src={logo} alt="Logo" className="img-fluid" width={160} />
                </Link>
            </div>
            {isLoading ? (
                <div className="voice-booking-card">
                    <div className="voice-widget-section">
                        <Spin size="large" />
                    </div>
                </div>
            ) : error && !businessData ? (
                <div className="voice-booking-card">
                    <div className="error-message">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10" strokeWidth="2" />
                            <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
                            <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" />
                        </svg>
                        <h2>Oops!</h2>
                        <p>{error}</p>
                        <button onClick={() => window.location.reload()} className="retry-button">
                            Try Again
                        </button>
                    </div>
                </div>
            ) : (
                <div className="voice-booking-card">
                    <div className="business-header">
                        {businessData?.pictures?.length > 0 && (
                            <img
                                src={businessData.pictures[0].photo_path}
                                alt={businessData.name}
                                className="business-logo"
                            />
                        )}
                        <h1 className="business-name">{businessData?.name || 'Our Business'}</h1>
                    </div>

                    <div className="voice-widget-section">

                        {error && (
                            <div className="agent-error">
                                <p>❌ {error}</p>
                            </div>
                        )}

                        {!isConnected && !isConnecting && (
                            <>
                                <div className="widget-intro">
                                    <p>Start talking with our AI assistant</p>
                                </div>
                                <div className="agent-orb-container">
                                    <div className="spinning-orb"></div>
                                    <button
                                        onClick={startConversation}
                                        className="orb-button"
                                        aria-label="Start voice booking"
                                    >
                                        <FaPhone />
                                    </button>
                                </div>
                                <p cassName="status-text">Ready to book your appointment</p>
                            </>
                        )}

                        {isConnecting && (
                            <div className="connecting">
                                <div className="connecting-orb">
                                    <div className="connecting-spinner"></div>
                                    <div className="orb-button">
                                        <FaPhone />
                                    </div>
                                </div>
                                <p>Connecting to agent...</p>
                            </div>
                        )}

                        {isConnected && (
                            <div className="conversation-active">
                                <div className="widget-intro">
                                    <p>Speak now to book your appointment</p>
                                </div>
                                <div className="listening-orb">
                                    <div className="listening-spinner"></div>
                                    <button
                                        onClick={endConversation}
                                        className="orb-button orb-button-stop"
                                        aria-label="End conversation"
                                    >
                                        <FaStop />
                                    </button>
                                </div>
                                <p className="status-text">Listening...</p>
                            </div>
                        )}

                        {/* Language Selector */}
                        <div className="preferences-section">
                            <div className="preference-item">
                                <label htmlFor="language-select">
                                    <FaGlobe className="preference-icon" />
                                    Language
                                </label>
                                <select
                                    id="language-select"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    disabled={isConnected || isConnecting}
                                    className="preference-select"
                                >
                                    <option value="English">English</option>
                                    <option value="Urdu">Urdu</option>
                                </select>
                            </div>

                        </div>
                    </div>

                    <div className="alternative-contact">
                        {businessData?.website && (
                            <a href={businessData.website} target="_blank" rel="noreferrer" aria-label="Visit website">
                                <TbWorldWww className="social-icon" />
                            </a>
                        )}
                        {businessData?.facebook && (
                            <a href={businessData.facebook} target="_blank" rel="noreferrer" aria-label="Visit Facebook">
                                <FaFacebook className="social-icon" />
                            </a>
                        )}
                        {businessData?.instagram && (
                            <a href={businessData.instagram} target="_blank" rel="noreferrer" aria-label="Visit Instagram">
                                <RiInstagramFill className="social-icon" />
                            </a>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Wrap with ConversationProvider
const VoiceBooking = () => {
    return (
        <ConversationProvider>
            <VoiceBookingContent />
        </ConversationProvider>
    );
};

export default VoiceBooking;
